/**
 * PortalReview - Vendor Portal Review Page
 *
 * Allows vendors to review their assessment results before final submission:
 * - 4-step progress bar (Upload → Assess → Review → Submit)
 * - Score gauges for each station and overall AI readiness
 * - Attention Required panel for critical findings
 * - Vendor response input for findings
 * - Accept Lower Tier option
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-554 (Phase 9)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BRANDING } from '../../../config/branding';
import { useParams, useNavigate } from 'react-router-dom';
import '../../../styles/vendor-portal.css';

// ============================================================================
// TYPES
// ============================================================================

type CertificationTier = 'platinum' | 'gold' | 'silver' | 'bronze' | null;
type FindingSeverity = 'critical' | 'major' | 'minor' | 'info';
type StationType = 'data_quality' | 'ml_readiness' | 'ontology' | 'fair';

interface PortalSession {
  assessment_id: string;
  vendor_name: string;
  product_name: string;
  status: string;
  deadline?: string;
}

interface StationScore {
  station: StationType;
  score: number;
  status: 'pass' | 'warning' | 'fail';
  findings_count: number;
  critical_count: number;
}

interface Finding {
  finding_id: string;
  station: StationType;
  severity: FindingSeverity;
  category: string;
  message: string;
  recommendation?: string;
  column?: string;
  requires_response: boolean;
  vendor_response?: string;
  response_timestamp?: string;
}

interface AssessmentResults {
  assessment_id: string;
  overall_score: number;
  projected_tier: CertificationTier;
  actual_tier?: CertificationTier;
  station_scores: StationScore[];
  findings: Finding[];
  assessment_complete: boolean;
  bridge_ai_decision?: {
    approved_for_gxp: boolean;
    rationale: string;
  };
  analyzed_at: string;
}

// ============================================================================
// CONFIGURATION
// ============================================================================

const STATION_LABELS: Record<StationType, string> = {
  data_quality: 'Data Quality',
  ml_readiness: 'ML Readiness',
  ontology: 'Ontology Alignment',
  fair: 'FAIR Compliance',
};

const STATION_ICONS: Record<StationType, string> = {
  data_quality: '📊',
  ml_readiness: '🤖',
  ontology: '🔗',
  fair: '📋',
};

const TIER_CONFIG: Record<string, { label: string; minScore: number; color: string; bgColor: string }> = {
  platinum: { label: 'Platinum', minScore: 90, color: '#166534', bgColor: '#dcfce7' },
  gold: { label: 'Gold', minScore: 80, color: '#a16207', bgColor: '#fef3c7' },
  silver: { label: 'Silver', minScore: 70, color: '#4b5563', bgColor: '#f3f4f6' },
  bronze: { label: 'Bronze', minScore: 60, color: '#92400e', bgColor: '#fef3c7' },
};

const SEVERITY_CONFIG: Record<FindingSeverity, { label: string; color: string; bgColor: string; icon: string }> = {
  critical: { label: 'Critical', color: '#dc2626', bgColor: '#fee2e2', icon: '🚨' },
  major: { label: 'Major', color: '#ea580c', bgColor: '#ffedd5', icon: '⚠️' },
  minor: { label: 'Minor', color: '#ca8a04', bgColor: '#fef3c7', icon: '📝' },
  info: { label: 'Info', color: '#2563eb', bgColor: '#dbeafe', icon: 'ℹ️' },
};

const RESPONSE_MAX_LENGTH = 2000;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getScoreColor(score: number): string {
  if (score >= 90) return '#166534';
  if (score >= 80) return '#16a34a';
  if (score >= 70) return '#ca8a04';
  if (score >= 60) return '#ea580c';
  return '#dc2626';
}

function formatTier(tier: CertificationTier): string {
  if (!tier) return 'Not Qualified';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function calculateTierFromScore(score: number): CertificationTier {
  if (score >= 90) return 'platinum';
  if (score >= 80) return 'gold';
  if (score >= 70) return 'silver';
  if (score >= 60) return 'bronze';
  return null;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Phase 9.1: Portal Progress Bar (4-step)
interface PortalProgressBarProps {
  currentStep: 'upload' | 'assess' | 'review' | 'submit';
}

const PortalProgressBar: React.FC<PortalProgressBarProps> = ({ currentStep }) => {
  const steps = [
    { key: 'upload', label: 'Upload Files', number: 1 },
    { key: 'assess', label: 'Assessment', number: 2 },
    { key: 'review', label: 'Review', number: 3 },
    { key: 'submit', label: 'Submit', number: 4 },
  ];

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0',
      padding: '24px 0',
      marginBottom: '24px',
    }}>
      {steps.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;

        return (
          <React.Fragment key={step.key}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 600,
                backgroundColor: isCompleted ? '#16a34a' : isCurrent ? '#2563eb' : '#e5e7eb',
                color: isCompleted || isCurrent ? '#fff' : '#6b7280',
                border: isCurrent ? '3px solid #93c5fd' : 'none',
                transition: 'all 0.2s ease',
              }}>
                {isCompleted ? '✓' : step.number}
              </div>
              <span style={{
                fontSize: '13px',
                fontWeight: isCurrent ? 600 : 400,
                color: isCurrent ? '#2563eb' : isCompleted ? '#16a34a' : '#6b7280',
              }}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div style={{
                width: '80px',
                height: '3px',
                backgroundColor: index < currentIndex ? '#16a34a' : '#e5e7eb',
                margin: '0 8px',
                marginBottom: '28px',
                transition: 'background-color 0.2s ease',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Phase 9.2: Score Gauge Component
interface ScoreGaugeProps {
  score: number;
  label: string;
  icon?: string;
  size?: 'sm' | 'md' | 'lg';
  showTier?: boolean;
}

const ScoreGauge: React.FC<ScoreGaugeProps> = ({ score, label, icon, size = 'md', showTier = false }) => {
  const sizes = {
    sm: { gauge: 80, stroke: 6, fontSize: 18, labelSize: 11 },
    md: { gauge: 120, stroke: 8, fontSize: 24, labelSize: 13 },
    lg: { gauge: 160, stroke: 10, fontSize: 32, labelSize: 15 },
  };

  const s = sizes[size];
  const radius = (s.gauge - s.stroke) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  const color = getScoreColor(score);
  const tier = calculateTierFromScore(score);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
    }}>
      <div style={{ position: 'relative', width: s.gauge, height: s.gauge }}>
        <svg width={s.gauge} height={s.gauge} style={{ transform: 'rotate(-90deg)' }}>
          {/* Background circle */}
          <circle
            cx={s.gauge / 2}
            cy={s.gauge / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={s.stroke}
          />
          {/* Score arc */}
          <circle
            cx={s.gauge / 2}
            cy={s.gauge / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={s.stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
        }}>
          {icon && <div style={{ fontSize: s.fontSize * 0.6, marginBottom: '2px' }}>{icon}</div>}
          <div style={{ fontSize: s.fontSize, fontWeight: 700, color }}>{score}%</div>
        </div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: s.labelSize, fontWeight: 500, color: '#374151' }}>{label}</div>
        {showTier && tier && (
          <div style={{
            fontSize: s.labelSize - 1,
            color: TIER_CONFIG[tier].color,
            marginTop: '2px',
          }}>
            {formatTier(tier)} Tier
          </div>
        )}
      </div>
    </div>
  );
};

// Phase 9.3: Attention Required Panel
interface AttentionRequiredPanelProps {
  findings: Finding[];
  onViewFinding: (findingId: string) => void;
}

const AttentionRequiredPanel: React.FC<AttentionRequiredPanelProps> = ({ findings, onViewFinding }) => {
  const criticalFindings = findings.filter((f) => f.severity === 'critical' && f.requires_response);
  const majorFindings = findings.filter((f) => f.severity === 'major' && f.requires_response);
  const unresolvedCount = findings.filter((f) => f.requires_response && !f.vendor_response).length;

  if (unresolvedCount === 0) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#dcfce7',
        borderRadius: '8px',
        border: '1px solid #86efac',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>✅</span>
          <div>
            <div style={{ fontWeight: 600, color: '#166534' }}>All Items Addressed</div>
            <div style={{ fontSize: '13px', color: '#15803d' }}>
              You have responded to all findings requiring attention.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#fef2f2',
      borderRadius: '8px',
      border: '1px solid #fecaca',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fee2e2',
        borderBottom: '1px solid #fecaca',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <span style={{ fontWeight: 600, color: '#991b1b' }}>Attention Required</span>
        </div>
        <span style={{
          padding: '2px 8px',
          backgroundColor: '#dc2626',
          color: '#fff',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
        }}>
          {unresolvedCount} item{unresolvedCount !== 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ padding: '12px 16px' }}>
        {criticalFindings.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', marginBottom: '6px' }}>
              Critical ({criticalFindings.length})
            </div>
            {criticalFindings.slice(0, 3).map((f) => (
              <div
                key={f.finding_id}
                onClick={() => onViewFinding(f.finding_id)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  marginBottom: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #fecaca',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{f.message}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{STATION_LABELS[f.station]}</div>
                </div>
                {!f.vendor_response && (
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '4px',
                  }}>
                    Needs Response
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {majorFindings.length > 0 && (
          <div>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#ea580c', marginBottom: '6px' }}>
              Major ({majorFindings.length})
            </div>
            {majorFindings.slice(0, 2).map((f) => (
              <div
                key={f.finding_id}
                onClick={() => onViewFinding(f.finding_id)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  marginBottom: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #fed7aa',
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', color: '#374151' }}>{f.message}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{STATION_LABELS[f.station]}</div>
                </div>
                {!f.vendor_response && (
                  <span style={{
                    fontSize: '11px',
                    padding: '2px 6px',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    borderRadius: '4px',
                  }}>
                    Needs Response
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Phase 9.4: Vendor Response Input
interface VendorResponseInputProps {
  finding: Finding;
  onSave: (findingId: string, response: string) => void;
  saving: boolean;
}

const VendorResponseInput: React.FC<VendorResponseInputProps> = ({ finding, onSave, saving }) => {
  const [response, setResponse] = useState(finding.vendor_response || '');
  const [isDirty, setIsDirty] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= RESPONSE_MAX_LENGTH) {
      setResponse(value);
      setIsDirty(value !== (finding.vendor_response || ''));
    }
  };

  const handleSave = () => {
    onSave(finding.finding_id, response);
    setIsDirty(false);
  };

  const severityConfig = SEVERITY_CONFIG[finding.severity];
  const remainingChars = RESPONSE_MAX_LENGTH - response.length;

  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
      }}>
        <span style={{ fontSize: '18px' }}>{severityConfig.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{
              padding: '2px 8px',
              backgroundColor: severityConfig.bgColor,
              color: severityConfig.color,
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
            }}>
              {severityConfig.label}
            </span>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{STATION_LABELS[finding.station]}</span>
          </div>
          <div style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>{finding.message}</div>
          {finding.recommendation && (
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
              <strong>Recommendation:</strong> {finding.recommendation}
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#374151', marginBottom: '6px' }}>
          Your Response {finding.requires_response && <span style={{ color: '#dc2626' }}>*</span>}
        </label>
        <textarea
          value={response}
          onChange={handleChange}
          placeholder="Explain how you plan to address this finding or provide context..."
          style={{
            width: '100%',
            minHeight: '100px',
            padding: '10px 12px',
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            fontSize: '14px',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '8px',
        }}>
          <span style={{
            fontSize: '12px',
            color: remainingChars < 100 ? '#dc2626' : '#6b7280',
          }}>
            {remainingChars} characters remaining
          </span>
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            style={{
              padding: '8px 16px',
              backgroundColor: isDirty ? '#2563eb' : '#e5e7eb',
              color: isDirty ? '#fff' : '#9ca3af',
              border: 'none',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: isDirty ? 'pointer' : 'default',
            }}
          >
            {saving ? 'Saving...' : finding.vendor_response ? 'Update Response' : 'Save Response'}
          </button>
        </div>
        {finding.response_timestamp && (
          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
            Last saved: {new Date(finding.response_timestamp).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

// Phase 9.5: Accept Lower Tier Component
interface AcceptLowerTierProps {
  projectedTier: CertificationTier;
  currentScore: number;
  onAccept: (tier: CertificationTier) => void;
  onRequestReview: () => void;
}

const AcceptLowerTier: React.FC<AcceptLowerTierProps> = ({
  projectedTier,
  currentScore,
  onAccept,
  onRequestReview,
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedTier, setSelectedTier] = useState<CertificationTier>(projectedTier);

  // Calculate available tiers
  const availableTiers = [
    { tier: 'platinum' as CertificationTier, score_requirement: 90, current_gap: 90 - currentScore, can_achieve: currentScore >= 90 },
    { tier: 'gold' as CertificationTier, score_requirement: 80, current_gap: 80 - currentScore, can_achieve: currentScore >= 80 },
    { tier: 'silver' as CertificationTier, score_requirement: 70, current_gap: 70 - currentScore, can_achieve: currentScore >= 70 },
    { tier: 'bronze' as CertificationTier, score_requirement: 60, current_gap: 60 - currentScore, can_achieve: currentScore >= 60 },
  ];

  const achievableTiers = availableTiers.filter((t) => t.can_achieve);
  const nextTierUp = availableTiers.find((t) => !t.can_achieve && t.current_gap > 0);

  if (!projectedTier) {
    return (
      <div style={{
        padding: '20px',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        border: '1px solid #fecaca',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <span style={{ fontSize: '24px' }}>⚠️</span>
          <div>
            <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '8px' }}>
              Score Below Certification Threshold
            </div>
            <div style={{ fontSize: '14px', color: '#7f1d1d', marginBottom: '12px' }}>
              Your current score of {currentScore}% does not meet the minimum requirement of 60% for Bronze certification.
              You need {60 - currentScore} more points to qualify.
            </div>
            <button
              onClick={onRequestReview}
              style={{
                padding: '10px 20px',
                backgroundColor: '#fff',
                color: '#991b1b',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Request Manual Review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    }}>
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <div style={{ fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
          Certification Tier Selection
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          Based on your score of {currentScore}%, you qualify for the following tiers:
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {achievableTiers.map((tierOption) => {
            const config = TIER_CONFIG[tierOption.tier!];
            const isSelected = selectedTier === tierOption.tier;
            const isProjected = tierOption.tier === projectedTier;

            return (
              <label
                key={tierOption.tier}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: isSelected ? config.bgColor : '#fff',
                  border: `2px solid ${isSelected ? config.color : '#e5e7eb'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <input
                  type="radio"
                  name="tier-selection"
                  checked={isSelected}
                  onChange={() => setSelectedTier(tierOption.tier)}
                  style={{ marginRight: '12px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 600, color: config.color }}>{config.label}</span>
                    {isProjected && (
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        backgroundColor: '#dbeafe',
                        color: '#2563eb',
                        borderRadius: '4px',
                      }}>
                        Recommended
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Minimum score: {config.minScore}%
                  </div>
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: currentScore >= config.minScore ? '#16a34a' : '#6b7280',
                }}>
                  {currentScore >= config.minScore ? '✓ Qualified' : 'Not Qualified'}
                </div>
              </label>
            );
          })}
        </div>

        {nextTierUp && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f0f9ff',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#0369a1',
          }}>
            <strong>Tip:</strong> You need {nextTierUp.current_gap} more points to qualify for {formatTier(nextTierUp.tier)} tier.
            Address the critical findings to improve your score.
          </div>
        )}

        {selectedTier !== projectedTier && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#fef3c7',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#92400e',
          }}>
            <strong>Note:</strong> You are selecting a lower tier than your projected qualification.
            This cannot be changed after submission.
          </div>
        )}

        <div style={{
          marginTop: '20px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
        }}>
          <button
            onClick={onRequestReview}
            style={{
              padding: '10px 20px',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Request Manual Review
          </button>
          <button
            onClick={() => setShowConfirmation(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: selectedTier ? TIER_CONFIG[selectedTier].color : '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Accept {formatTier(selectedTier)} Tier
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
          }}>
            <div style={{ fontWeight: 600, fontSize: '18px', color: '#374151', marginBottom: '12px' }}>
              Confirm Tier Selection
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
              You are accepting <strong style={{ color: selectedTier ? TIER_CONFIG[selectedTier].color : '#374151' }}>{formatTier(selectedTier)}</strong> certification.
              {selectedTier !== projectedTier && (
                <span> This is lower than your qualified tier of {formatTier(projectedTier)}.</span>
              )}
              <br /><br />
              This selection cannot be changed after submission.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onAccept(selectedTier);
                  setShowConfirmation(false);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: selectedTier ? TIER_CONFIG[selectedTier].color : '#6b7280',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const PortalReview: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<PortalSession | null>(null);
  const [results, setResults] = useState<AssessmentResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingResponse, setSavingResponse] = useState(false);
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);
  const [acceptedTier, setAcceptedTier] = useState<CertificationTier>(null);
  const [submitting, setSubmitting] = useState(false);

  // Validate token and load session
  useEffect(() => {
    const loadData = async () => {
      if (!token) {
        navigate('/portal/invalid');
        return;
      }

      try {
        // Validate token
        const validateRes = await fetch(`/api/portal/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!validateRes.ok) {
          navigate(`/portal/${token}`);
          return;
        }

        const sessionData = await validateRes.json();
        setSession(sessionData);

        // Load assessment results
        const resultsRes = await fetch(`/api/portal/${token}/results`);
        if (!resultsRes.ok) {
          if (resultsRes.status === 404) {
            setError('Assessment results are not yet available. Please check back later.');
          } else {
            throw new Error('Failed to load assessment results');
          }
          setLoading(false);
          return;
        }

        const resultsData = await resultsRes.json();
        setResults(resultsData);
        setAcceptedTier(resultsData.projected_tier);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [token, navigate]);

  // Save vendor response
  const handleSaveResponse = useCallback(async (findingId: string, response: string) => {
    if (!token) return;

    setSavingResponse(true);
    try {
      const res = await fetch(`/api/portal/${token}/findings/${findingId}/response`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response }),
      });

      if (!res.ok) {
        throw new Error('Failed to save response');
      }

      // Update local state
      setResults((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          findings: prev.findings.map((f) =>
            f.finding_id === findingId
              ? { ...f, vendor_response: response, response_timestamp: new Date().toISOString() }
              : f
          ),
        };
      });
    } catch {
      setError('Failed to save response');
    } finally {
      setSavingResponse(false);
    }
  }, [token]);

  // Accept tier
  const handleAcceptTier = useCallback((tier: CertificationTier) => {
    setAcceptedTier(tier);
  }, []);

  // Request manual review
  const handleRequestReview = useCallback(async () => {
    if (!token) return;

    try {
      await fetch(`/api/portal/${token}/request-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      alert('Manual review requested. Our team will contact you.');
    } catch {
      setError('Failed to request review');
    }
  }, [token]);

  // Submit final assessment
  const handleSubmit = useCallback(async () => {
    if (!token || !results || !acceptedTier) return;

    // Check if all required responses are provided
    const unresolvedFindings = results.findings.filter(
      (f) => f.requires_response && !f.vendor_response
    );

    if (unresolvedFindings.length > 0) {
      setError(`Please respond to all ${unresolvedFindings.length} required findings before submitting.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/portal/${token}/submit-final`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted_tier: acceptedTier }),
      });

      if (!res.ok) {
        throw new Error('Submission failed');
      }

      navigate(`/portal/${token}/complete`);
    } catch {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token, results, acceptedTier, navigate]);

  // View finding details
  const handleViewFinding = (findingId: string) => {
    setSelectedFindingId(findingId);
    // Scroll to findings section
    document.getElementById('findings-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-loading">
            <div className="spinner" />
            <p>Loading assessment results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!session || !results) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-error">
            <div className="error-icon">⚠️</div>
            <h1>Unable to Load Results</h1>
            <p>{error || 'Assessment results are not available.'}</p>
            <button className="btn btn-primary" onClick={() => navigate(`/portal/${token}/upload`)}>
              Return to Upload
            </button>
          </div>
        </div>
      </div>
    );
  }

  const unresolvedCount = results.findings.filter(
    (f) => f.requires_response && !f.vendor_response
  ).length;

  const canSubmit = unresolvedCount === 0 && acceptedTier !== null && !submitting;

  const selectedFinding = selectedFindingId
    ? results.findings.find((f) => f.finding_id === selectedFindingId)
    : null;

  return (
    <div className="portal-page">
      <div className="portal-container wide">
        {/* Header */}
        <div className="portal-header">
          <div className="portal-logo">
            <span className="logo-icon">🧭</span>
            <span className="logo-text">{BRANDING.productName}</span>
          </div>
          <div className="portal-context">
            <span className="vendor-name">{session.vendor_name}</span>
            <span className="product-name">{session.product_name}</span>
          </div>
        </div>

        {/* Progress Bar (Phase 9.1) */}
        <PortalProgressBar currentStep="review" />

        {/* Error Banner */}
        {error && (
          <div className="portal-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Main Content */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          {/* Left Column - Scores and Findings */}
          <div>
            {/* Overall Score Section (Phase 9.2) */}
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#374151', marginBottom: '20px' }}>
                Assessment Results
              </h2>
              <div style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '24px',
              }}>
                {/* Overall AI Readiness Gauge */}
                <ScoreGauge
                  score={results.overall_score}
                  label="AI Readiness Score"
                  size="lg"
                  showTier
                />

                {/* Station Scores */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '20px',
                }}>
                  {results.station_scores.map((station) => (
                    <ScoreGauge
                      key={station.station}
                      score={station.score}
                      label={STATION_LABELS[station.station]}
                      icon={STATION_ICONS[station.station]}
                      size="sm"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Bridge AI Decision */}
            {results.bridge_ai_decision && (
              <div style={{
                backgroundColor: results.bridge_ai_decision.approved_for_gxp ? '#dcfce7' : '#fef2f2',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                border: `1px solid ${results.bridge_ai_decision.approved_for_gxp ? '#86efac' : '#fecaca'}`,
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: results.bridge_ai_decision.approved_for_gxp ? '#166534' : '#991b1b',
                  marginBottom: '8px',
                }}>
                  Bridge AI Decision
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <span style={{
                    padding: '4px 10px',
                    backgroundColor: results.bridge_ai_decision.approved_for_gxp ? '#166534' : '#dc2626',
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}>
                    {results.bridge_ai_decision.approved_for_gxp ? '✓ Approved for GxP Use' : '✕ Requires Remediation'}
                  </span>
                </div>
                <p style={{
                  fontSize: '14px',
                  color: results.bridge_ai_decision.approved_for_gxp ? '#15803d' : '#7f1d1d',
                  margin: 0,
                }}>
                  {results.bridge_ai_decision.rationale}
                </p>
              </div>
            )}

            {/* Findings Section (Phase 9.4) */}
            <div id="findings-section" style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#374151', marginBottom: '20px' }}>
                Findings ({results.findings.length})
              </h2>

              {selectedFinding ? (
                <div>
                  <button
                    onClick={() => setSelectedFindingId(null)}
                    style={{
                      marginBottom: '16px',
                      padding: '6px 12px',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    ← Back to all findings
                  </button>
                  <VendorResponseInput
                    finding={selectedFinding}
                    onSave={handleSaveResponse}
                    saving={savingResponse}
                  />
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {results.findings.length === 0 ? (
                    <div style={{
                      padding: '32px',
                      textAlign: 'center',
                      color: '#6b7280',
                    }}>
                      <span style={{ fontSize: '32px', display: 'block', marginBottom: '12px' }}>✅</span>
                      <p>No findings! Your data passed all quality checks.</p>
                    </div>
                  ) : (
                    results.findings.map((finding) => {
                      const config = SEVERITY_CONFIG[finding.severity];
                      return (
                        <div
                          key={finding.finding_id}
                          onClick={() => setSelectedFindingId(finding.finding_id)}
                          style={{
                            padding: '12px 16px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: '1px solid #e5e7eb',
                            transition: 'border-color 0.2s',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                              <span style={{ fontSize: '16px' }}>{config.icon}</span>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  <span style={{
                                    padding: '2px 6px',
                                    backgroundColor: config.bgColor,
                                    color: config.color,
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                  }}>
                                    {config.label}
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {STATION_LABELS[finding.station]}
                                  </span>
                                </div>
                                <div style={{ fontSize: '14px', color: '#374151' }}>{finding.message}</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {finding.requires_response && !finding.vendor_response && (
                                <span style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                }}>
                                  Response Required
                                </span>
                              )}
                              {finding.vendor_response && (
                                <span style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#dcfce7',
                                  color: '#166534',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                }}>
                                  Responded ✓
                                </span>
                              )}
                              <span style={{ color: '#6b7280' }}>→</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Attention & Tier Selection */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Attention Required Panel (Phase 9.3) */}
            <AttentionRequiredPanel
              findings={results.findings}
              onViewFinding={handleViewFinding}
            />

            {/* Tier Selection (Phase 9.5) */}
            <AcceptLowerTier
              projectedTier={results.projected_tier}
              currentScore={results.overall_score}
              onAccept={handleAcceptTier}
              onRequestReview={handleRequestReview}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="portal-footer-actions" style={{ marginTop: '24px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/portal/${token}/upload`)}
            disabled={submitting}
          >
            ← Back to Files
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {unresolvedCount > 0 && (
              <span style={{ fontSize: '13px', color: '#dc2626' }}>
                {unresolvedCount} finding{unresolvedCount !== 1 ? 's' : ''} require response
              </span>
            )}
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? 'Submitting...' : 'Submit Final Assessment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalReview;
